import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../../../../app/store";
import {botApi, RequestStatus} from "../../../../../api/apiConfiguration";
import {
    ConfigurationRegion,
    Em2DepartmentHashMappingDTO,
    Em2EnquiryTypeMappingDTO,
    EnquiryProvider,
    LeadSettingsResponse,
    LocationLeadConfigRequest,
    UpdateBotLeadSettingsRequest
} from "../../../../../api/generated";
import {dedupeEnquiryTypeMappings} from "./util/em2EnquiryTypeMappingUtil";

export interface UpdateLeadSettingsRequestUI extends UpdateBotLeadSettingsRequest {
    locationLeadConfigs: LocationLeadConfigRequest[];
}

interface LeadsState {
    leadSettings?: LeadSettingsResponse;
    updateRequest: UpdateLeadSettingsRequestUI;
    leadSettingsRequestStatus: RequestStatus;
    updateLeadSettingsStatus: RequestStatus;
    hasUnsavedChanges: boolean;
}

const initialState: LeadsState = {
    updateRequest: {
        botPublicId: "",
        enquiryProvider: EnquiryProvider.NoProvider,
        locationLeadConfigs: []
    },
    leadSettingsRequestStatus: RequestStatus.NONE,
    updateLeadSettingsStatus: RequestStatus.NONE,
    hasUnsavedChanges: false
};

const mapLeadSettingsResponseToRequest = (
    payload: LeadSettingsResponse,
    botPublicId: string
): UpdateLeadSettingsRequestUI => ({
    botPublicId,
    enquiryProvider: payload.enquiryProvider,
    locationLeadConfigs: payload.locationLeadConfigs || [],
    enquiryTypeMappings: dedupeEnquiryTypeMappings(payload.enquiryTypeMappings)
});

export const fetchLeadSettingsById = createAsyncThunk(
    "leads/fetchById",
    async (botPublicId: string) => {
        return await botApi.getBotLeadSettings({botPublicId});
    }
);

export const updateLeadSettings = createAsyncThunk<LeadSettingsResponse, string, { state: RootState }>(
    "leads/update",
    (botPublicId, {getState}) => {
        const request = getState().leads.updateRequest;
        return botApi.updateBotLeadSettings({
            updateBotLeadSettingsRequest: {
                ...request,
                botPublicId
            }
        });
    }
);

export const leadsSlice = createSlice({
    name: "leads",
    initialState,
    reducers: {
        updateLeadSettingsRequest: (state, {payload}: PayloadAction<Partial<UpdateLeadSettingsRequestUI>>) => {
            state.updateRequest = {
                ...state.updateRequest,
                ...payload
            };
            state.hasUnsavedChanges = true;
        },
        updateLocationLeadConfig: (state, {payload}: PayloadAction<{
            locationPublicId: string;
            field: "dragon2000DealerId" | "locationHash" | "groupHash" | "franchiseHash" | "em2Region" | "leadSummaryEnabled" | "departmentHashMappings";
            value: string | boolean | ConfigurationRegion | Em2DepartmentHashMappingDTO[];
        }>) => {
            const {locationPublicId, field, value} = payload;
            state.updateRequest.locationLeadConfigs = state.updateRequest.locationLeadConfigs.map(loc =>
                loc.locationPublicId === locationPublicId
                    ? {...loc, [field]: value}
                    : loc
            );
            state.hasUnsavedChanges = true;
        },
        resetLeadSettings: (state) => {
            if (state.leadSettings) {
                state.updateRequest = mapLeadSettingsResponseToRequest(state.leadSettings, state.updateRequest.botPublicId);
                state.hasUnsavedChanges = false;
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchLeadSettingsById.pending, (state) => {
            state.leadSettingsRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchLeadSettingsById.fulfilled, (state, {payload, meta}) => {
            state.leadSettings = payload;
            state.leadSettingsRequestStatus = RequestStatus.RESOLVED;
            state.updateRequest = mapLeadSettingsResponseToRequest(payload, meta.arg);
            state.hasUnsavedChanges = false;
        });
        builder.addCase(fetchLeadSettingsById.rejected, (state) => {
            state.leadSettingsRequestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(updateLeadSettings.pending, (state) => {
            state.updateLeadSettingsStatus = RequestStatus.PENDING;
        });
        builder.addCase(updateLeadSettings.fulfilled, (state, {payload, meta}) => {
            state.leadSettings = payload;
            state.updateLeadSettingsStatus = RequestStatus.RESOLVED;
            state.updateRequest = mapLeadSettingsResponseToRequest(payload, meta.arg);
            state.hasUnsavedChanges = false;
        });
        builder.addCase(updateLeadSettings.rejected, (state) => {
            state.updateLeadSettingsStatus = RequestStatus.REJECTED;
        });
    }
});

export const {
    updateLeadSettingsRequest,
    updateLocationLeadConfig,
    resetLeadSettings
} = leadsSlice.actions;

export const leadSettingsRequestStatusSelector = (state: RootState): RequestStatus => state.leads.leadSettingsRequestStatus;
export const updateLeadSettingsStatusSelector = (state: RootState): RequestStatus => state.leads.updateLeadSettingsStatus;
export const hasUnsavedLeadSettingsChangesSelector = (state: RootState): boolean => state.leads.hasUnsavedChanges;
export const selectLeadProvider = (state: RootState): EnquiryProvider => state.leads.updateRequest.enquiryProvider;
export const selectLocationLeadConfigs = (state: RootState): LocationLeadConfigRequest[] => state.leads.updateRequest.locationLeadConfigs || [];
export const selectLocationDragon2000DealerId = (locationPublicId: string) => (state: RootState): string | undefined =>
    state.leads.updateRequest.locationLeadConfigs.find(loc => loc.locationPublicId === locationPublicId)?.dragon2000DealerId;
export const selectEm2Region = (locationPublicId: string) => (state: RootState): ConfigurationRegion | undefined =>
    state.leads.updateRequest.locationLeadConfigs.find(loc => loc.locationPublicId === locationPublicId)?.em2Region;
export const selectEnquiryTypeMappings = (state: RootState): Em2EnquiryTypeMappingDTO[] =>
    state.leads.updateRequest.enquiryTypeMappings || [];
export const selectDepartmentHashMappings = (locationPublicId: string) => (state: RootState): Em2DepartmentHashMappingDTO[] =>
    state.leads.updateRequest.locationLeadConfigs.find(loc => loc.locationPublicId === locationPublicId)?.departmentHashMappings || [];

export default leadsSlice.reducer;
